/**
 * useOrgRequests hook
 *
 * Fetches paginated org-scoped requests from /api/tenant/requests.
 * Supports status, bundesland, and search filters.
 *
 * Note: Not using useApiQuery because paginated endpoints return
 * both `data` and `pagination` fields which need separate extraction.
 */

"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import type { RequestNeed } from "@/lib/requests/needs";
import type { OrgRequestFilters } from "@/lib/requests/types";
import type { ResponsibleMember } from "@/lib/projects/types";
export interface OrgRequest {
  id: string;
  /** The concurrency token a command on this request is sent against. */
  revision: number;
  status: string;
  bundesland: string | null;
  landkreis: string | null;
  plz: string | null;
  /** What the request needs: its lines, each in its own unit (lib/requests/needs.ts). */
  lines: RequestNeed[];
  organization_id: string | null;
  created_at: string;
  updated_at: string;
  naturraum: {
    id: string;
    zone_code: string;
    name: string;
  } | null;
  project: {
    id: string;
    name: string;
  } | null;
  /** Live-derived from the request's project (Pill γ / 869dj704f). */
  responsible_members: ResponsibleMember[];
}
export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// OrgRequestFilters re-exported from @/lib/requests/types
export type { OrgRequestFilters };
function buildUrl(base: string, filters: OrgRequestFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.bundesland) params.set("bundesland", filters.bundesland);
  if (filters.search) params.set("search", filters.search);
  if (filters.page) params.set("page", String(filters.page));
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}
export function useOrgRequests() {
  const [requests, setRequests] = useState<OrgRequest[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<OrgRequestFilters>({});
  const url = useMemo(() => buildUrl("/api/tenant/requests", filters), [filters]);
  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Anfragen konnten nicht geladen werden");
      }
      const json = await response.json();
      setRequests(json.data);
      setPagination(json.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ein unerwarteter Fehler ist aufgetreten");
    } finally {
      setIsLoading(false);
    }
  }, [url]);
  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);
  return {
    requests,
    pagination,
    isLoading,
    error,
    filters,
    setFilters,
    refresh: fetchRequests
  };
}